import FavoriteIcon from '@mui/icons-material/Favorite';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import FavoriteBorderRoundedIcon from '@mui/icons-material/FavoriteBorderRounded';
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded';
import {
  Box,
  CircularProgress,
  IconButton,
  SxProps,
  Theme,
  useTheme
} from '@mui/material';

import HeartIcon from '@/shared/ui/icons/Heart.svg';
import HeartIconEmpty from '@/shared/ui/icons/HeartEmpty.svg';

import { ON_DARK_FOCUS_RING_SX } from '../../styles/onDarkButtons';
import { TooltipDark } from '../tooltip/Dark';
import { guardContainingBlockSx } from './guardContainingBlockSx';
import { getPhotoChromeSx } from './photoChromeSx';

/** Тексты тултипа/aria-label по умолчанию — карточки актёра/кастинга */
const DEFAULT_TOOLTIP = {
  active: 'Удалить из избранного',
  inactive: 'Добавить в избранное'
};

interface IProps {
  isBookmarked: boolean;
  onClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
  isLoading: boolean;
  sx?: SxProps<Theme>;
  surface?: 'photo' | 'light' | 'dark';
  /**
   * Тексты тултипа/aria-label по умолчанию — «Добавить в избранное» /
   * «Удалить из избранного» (карточки). Профиль актёра под режиссёром (Task 16)
   * просит другие формулировки — «В избранное» / «Убрать из избранного»
   */
  tooltip?: { active: string; inactive: string };
}

/**
 * Компонент: кнопка сердце, для добавления и удаления из избранного
 * @param root0 - пропсы
 * @param root0.isBookmarked - в избранном или нет
 * @param root0.onClick - ф-я, которая срабатывает при клике на кнопку
 * @param root0.isLoading - загрузка
 * @param root0.sx - стили
 * @param root0.surface - поверхность под кнопкой: `photo` (по умолчанию), `light` или `dark`
 *   (тёмная полоса героя анкеты: пустое сердце белое, наведение — светлый фон)
 * @param root0.tooltip - необязательные тексты тултипа/aria-label вместо дефолтных
 */
export const FavoriteHeartButton = ({
  isBookmarked,
  onClick,
  isLoading,
  sx,
  surface = 'photo',
  tooltip
}: IProps) => {
  const theme = useTheme();
  const { active, inactive } = { ...DEFAULT_TOOLTIP, ...tooltip };
  const label = isBookmarked ? active : inactive;

  /**
   * Рендер иконки сердца.
   *
   * На светлой поверхности проектные svg не годятся: `HeartEmpty.svg` нарисован
   * белой обводкой под тёмное фото (`stroke="white"` захардкожен и currentColor не
   * слушает), поэтому на белой строке списка пустое сердце попросту исчезало.
   * Для таких поверхностей берём иконки MUI — они красятся темой.
   *
   * На фото сердце стоит в общей обёртке `photo-chrome` (см.
   * `getPhotoChromeSx`) — тот же круг 40×40, что и у кнопки «в папку»
   * (`BookmarkFolderButton`). Раньше круг вешался прямо на сам svg: у сердца
   * холст 41×40, поэтому вместо круга получался овал.
   */
  const renderIcon = () => {
    if (surface === 'light' || surface === 'dark') {
      const iconSx = {
        fontSize: 24,
        color: isBookmarked
          ? 'error.main'
          : surface === 'dark'
            ? 'common.white'
            : 'action.active'
      };

      // Тёмная полоса героя — прямые контуры макета, на светлом — скруглённые
      const Filled = surface === 'dark' ? FavoriteIcon : FavoriteRoundedIcon;
      const Border =
        surface === 'dark' ? FavoriteBorderIcon : FavoriteBorderRoundedIcon;

      return isBookmarked ? <Filled sx={iconSx} /> : <Border sx={iconSx} />;
    }

    return (
      <Box data-testid='photo-chrome' sx={getPhotoChromeSx(theme)}>
        {isBookmarked ? <HeartIcon /> : <HeartIconEmpty />}
      </Box>
    );
  };
  return (
    <IconButton
      aria-label={label}
      sx={{
        position: 'absolute',
        top: 0,
        right: 0,
        zIndex: 3,
        // Круг живёт внутри `photo-chrome` (40×40, см. `renderIcon`), сама
        // кнопка — только тач-цель 44×44 без собственного паддинга.
        // Резерв под кнопку в блоке имени карточки человека считается от этого
        // размера — см. HEART_RESERVE в entities/user/ui/Card.tsx.
        p: 0,
        minWidth: 44,
        minHeight: 44,
        color: isBookmarked ? 'error.main' : 'text.main',
        // `brightness(1.2)` осветляет иконку — это работает только поверх фото, где
        // сердце белое. На светлой поверхности иконка тёмная, и осветление СНИЖАЛО бы
        // её контраст вместо акцента: там наведение отмечает сам IconButton своим
        // фоном, а иконка густеет до основного цвета текста.
        ...(surface === 'photo'
          ? { '&:hover svg': { filter: 'brightness(1.2)' } }
          : surface === 'dark'
            ? {
                ...ON_DARK_FOCUS_RING_SX,
                '&:hover': {
                  // Макет: у закрашенного сердца наведение — индиго 4 %, у пустого — белое 8 %
                  bgcolor: isBookmarked
                    ? 'rgba(63, 81, 181, 0.04)'
                    : 'rgba(255, 255, 255, 0.08)'
                }
              }
            : {
                '&:hover .MuiSvgIcon-root': {
                  color: isBookmarked ? 'error.dark' : 'text.primary'
                }
              }),
        ...guardContainingBlockSx(sx)
      }}
      onClick={e => onClick(e)}
    >
      {isLoading ? (
        <CircularProgress size={40} />
      ) : (
        <TooltipDark title={label} arrow placement='top'>
          {renderIcon()}
        </TooltipDark>
      )}
    </IconButton>
  );
};
